import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  initLocalization,
  InitLocalization,
  LoadLocalizationSuccess,
  LOCALIZATION_KEYS,
  LocalizationActionsEnum,
  SetLocaleSuccess,
} from '../../../localization/store';
import { of } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { concat } from 'rxjs/internal/observable/concat';
import { catchError, flatMap, map, take } from 'rxjs/operators';
import { AppService } from '../../services';
import {
  AppActionsEnum,
  FetchLocationData,
  fetchLocationData,
  FetchLocationDataError,
  fetchLocationDataError,
  initApp,
  InitApp,
  InitErrorHandler,
  setCookie,
  SetCookie,
  setLocationData,
  SetLocationData,
} from '../actions';
import { GeoLocationData } from '../models/geo-location-data.model';
import { ErrorHandlerService } from 'i2g-ng-utils';

@Injectable()
export class AppEffects {
  constructor(
    private _actions: Actions,
    private _errorHandlerService: ErrorHandlerService,
    private _appService: AppService
  ) {}

  @Effect()
  initErrorHandler(): Observable<InitLocalization> {
    return this._actions.pipe(
      ofType(AppActionsEnum.INIT_ERROR_HANDLER),
      take(1),
      map(({ payload }: InitErrorHandler) => {
        const { raygunAPIKey, debugMode, version } = payload.environment;
        this._errorHandlerService.init(raygunAPIKey, debugMode, version);
        return initLocalization();
      })
    );
  }

  @Effect()
  loadLocalizationSuccess(): Observable<
    InitApp | FetchLocationData | SetCookie
  > {
    return this._actions.pipe(
      ofType(LocalizationActionsEnum.LOAD_LOCALIZATION_SUCCESS),
      take(1),
      flatMap(({ payload }: LoadLocalizationSuccess) => {
        return concat([
          setCookie(LOCALIZATION_KEYS.LOCALE, payload.currentLocale),
          setCookie(LOCALIZATION_KEYS.COUNTRY, payload.country),
          initApp(),
          fetchLocationData(),
        ]);
      })
    );
  }

  @Effect()
  setLocale(): Observable<SetCookie> {
    return this._actions.pipe(
      ofType(LocalizationActionsEnum.SET_LOCALE_SUCCESS),
      map(({ payload }: SetLocaleSuccess) =>
        setCookie(LOCALIZATION_KEYS.LOCALE, payload.locale)
      )
    );
  }

  @Effect()
  fetchLocationData(): Observable<SetLocationData | FetchLocationDataError> {
    return this._actions.pipe(
      ofType(AppActionsEnum.FETCH_LOCATION_DATA),
      flatMap(() => {
        return this._appService.getGeoLocation().pipe(
          map((geoLocation: GeoLocationData) => setLocationData(geoLocation)),
          catchError((error) => of(fetchLocationDataError(error)))
        );
      })
    );
  }
}
